import { DateTime } from "luxon";
import { ObjectPKInput, ObjectSKInput } from "../aws/DynamoDbApiTypes";
import { filter } from "../util/Helpers";
import { meanAverage } from "../util/Math";
import { Serializable } from "../util/Serialization";
import { BasicError } from "./BasicError";
import { StripUnderscore, UnderscoreKeyNames, UnderscoreKeys } from "../util/Types";
import z from "zod";

const dynamoKeySchema = z.object({
  PK: z.string().nonempty(),
  SK: z.string().nonempty(),
});
export type DynamoKey = z.infer<typeof dynamoKeySchema>;
export type UserOwnedDynamoKey = DynamoKey;
export type GSIKey = `GSI${number}${"PK" | "SK"}`;
export type GlobalIndices = Partial<Record<GSIKey, string>>;
export type DynamoKeys = { _className: string } & DynamoKey;
export type DynamoFieldValues<TFields extends Record<string, any>> = Partial<Record<GSIKey, string>> &
	Partial<Record<UnderscoreKeyNames<TFields>, TFields[UnderscoreKeyNames<TFields>]>>;

export type DynamoObject<TFields extends Record<string, any>> = DynamoKeys & DynamoFieldValues<TFields>;

export const baseDynamoSchema = z.object({
  _counts: z.record(z.number()),              // { [key: string]: number }
  _countChanges: z.record(z.number()),        // same
  _parentCountChanges: z.record(z.number()),  // same
  _dirty: z.boolean(),
  _dirtyFields: z.array(z.string().nonempty()),
  _createdDate: z.number(),
  _updatedDate: z.number(),
  _className: z.string().nonempty(),
  _lockExpires: z.number().optional(),
  _status: z.number(),
  _createdBy: dynamoKeySchema.optional(),
  _updatedBy: dynamoKeySchema.optional(),
  _lockKey: z.string().optional(),
  _lockOwner: z.number().optional(),
}).passthrough();

export type BaseDynamoFields = StripUnderscore<z.infer<typeof baseDynamoSchema>>;
export abstract class BaseDynamoClass<TFields extends BaseDynamoFields = BaseDynamoFields> implements Serializable {
	private _counts: { [key: string]: number } = {};
	private _countChanges: { [key: string]: number } = {};
	private _parentCountChanges: { [key: string]: number } = {};
	private _dirty: boolean = false;
	private _dirtyFields: UnderscoreKeyNames<TFields>[] = [];
	private _createdDate: number = Date.now();
	private _updatedDate: number = Date.now();
	_className: string = "BaseDynamoClass";
	_deserializeClassName: string = "BaseDynamoClass";
	private _createdBy?: DynamoKey;
	private _updatedBy?: DynamoKey;
	//Lock key is to avoid short term race conditions occurring.
	private _lockKey?: string;
	//Lock owner is to allow longer running operations to hold lock on object. (Datetime in ms that the lock expires)
	private _lockOwner?: number;
	private _lockExpires?: number;
	protected _status: number | undefined;

	constructor(props?: Partial<TFields>) {
		if (props) {
			const underscoreProps = this.underscoreKeys(props);
			Object.assign(this, underscoreProps);
		}
	}
	underscoreKeys<TFields extends Record<string, any>>(obj: Partial<TFields>): Partial<UnderscoreKeys<TFields>> {
		const result: Partial<UnderscoreKeys<TFields>> = {};

		for (const key in obj) {
			if (obj[key] !== undefined) {
				const underscoredKey = `_${key}` as keyof UnderscoreKeys<TFields>;
				result[underscoredKey] = obj[key] as any;
			}
		}
		return result;
	}

	getFieldList(): UnderscoreKeyNames<TFields>[] {
		return [
			"_counts",
			"_countChanges",
			"_parentCountChanges",
			"_dirty",
			"_dirtyFields",
			"_createdDate",
			"_updatedDate",
			"_className",
			"_lockExpires",
			"_status",
			"_createdBy",
			"_updatedBy",
			"_lockKey",
			"_lockOwner",
		];
	}
	get counts(): { [key: string]: number } {
		return this.checkFieldType<"counts">(this._counts);
	}
	get countChanges(): { [key: string]: number } {
		return this.checkFieldType<"countChanges">(this._countChanges);
	}
	get parentCountChanges(): { [key: string]: number } {
		return this.checkFieldType<"parentCountChanges">(this._parentCountChanges);
	}
	get dirty(): boolean {
		return this.checkFieldType<"dirty">(this._dirty);
	}
	get dirtyFields(): string[] {
		return this.checkFieldType<"dirtyFields">(this._dirtyFields);
	}
	get createdDate(): number {
		return this.checkFieldType<"createdDate">(this._createdDate);
	}
	get updatedDate(): number {
		return this.checkFieldType<"updatedDate">(this._updatedDate);
	}
	get className(): string {
		return this.checkFieldType<"className">(this._className);
	}
	get createdBy(): DynamoKey | undefined {
		return this.checkFieldType<"createdBy">(this._createdBy);
	}
	set createdBy(value: DynamoKey | undefined) {
		this._createdBy = this.checkFieldTypeAndMark<"createdBy">("_createdBy", value);
	}
	get updatedBy(): DynamoKey | undefined {
		return this.checkFieldType<"updatedBy">(this._updatedBy);
	}
	set updatedBy(value: DynamoKey | undefined) {
		this._updatedBy = this.checkFieldTypeAndMark<"updatedBy">("_updatedBy", value);
	}
	get lockKey(): string | undefined {
		return this.checkFieldType<"lockKey">(this._lockKey);
	}
	set lockKey(value: string | undefined) {
		this._lockKey = this.checkFieldTypeAndMark<"lockKey">("_lockKey", value);
	}
	get lockOwner(): number | undefined {
		return this.checkFieldType<"lockOwner">(this._lockOwner);
	}
	set lockOwner(value: number | undefined) {
		this._lockOwner = this.checkFieldTypeAndMark<"lockOwner">("_lockOwner", value);
	}
	get lockExpires(): number | undefined {
		return this.checkFieldType<"lockExpires">(this._lockExpires);
	}
	set lockExpires(value: number | undefined) {
		this._lockExpires = this.checkFieldTypeAndMark<"lockExpires">("_lockExpires", value);
	}
	protected checkFieldType<K extends keyof TFields>(value: TFields[K]): TFields[K] {
		return value as any;
	}
	protected checkFieldTypeAndMark<K extends keyof TFields>(fieldKey: UnderscoreKeyNames<TFields>, value: TFields[K]) {
		this.markFieldDirty(fieldKey);
		return value as any;
	}
	protected assertFetched<K extends keyof TFields>(key: K): asserts this is this & Required<Pick<TFields, K>> {
		if (!(key in this)) {
			throw new Error(`Field "${String(key)}" was not fetched in this projection.`);
		}
	}
	getIgnoreFields(): string[] {
		return ["_deserializeClassName"];
	}

	static splitPrefix(key: string): string {
		return key.split("#")[0];
	}
	abstract getPKPrefix(): string;
	abstract getSKPrefix(props: object | undefined): string;
	isPermissionCheckNeeded() {
		return true;
	}
	abstract getPK(): string;
	abstract getSK(): string;
	getKey(): DynamoKey {
		return {
			PK: this.getPK(),
			SK: this.getSK(),
		};
	}
	getKeyString(): string {
		return this.getPK() + "#" + this.getSK();
	}
	abstract getParentKey(): DynamoKey | undefined;
	abstract getParentKeyInput(): ObjectPKInput | undefined;
	abstract getParentTableName(): string | undefined;
	abstract generateKey(input: any): DynamoKey;
	abstract generatePK(input: ObjectPKInput): string;
	abstract generateGSIPK(index: number, key: ObjectPKInput): string;
	abstract generateGSISK(index: number, key: ObjectSKInput | undefined): string;
	abstract getGlobalIndices(): GlobalIndices;
	getDynamoObject(): DynamoObject<TFields> {
		const obj: DynamoObject<TFields> = {
			_className: this._className,
			PK: this.getPK(),
			SK: this.getSK(),
			...this.getAllFieldValues(),
		};
		return obj;
	}
	getAllFieldValues(): DynamoFieldValues<TFields> {
		const obj: Record<string, any> = {
			...this.getGlobalIndices(),
		};
		for (const fieldName of this.getFieldList()) {
			if (fieldName in this) {
				obj[fieldName] = (this as any)[fieldName];
			}
		}

		return obj;
	}
	getFieldValuesForUpdate(): DynamoFieldValues<TFields> {
		const obj: Record<string, any> = {
			...this.getGlobalIndices(),
		};

		if (this._dirtyFields.length > 0) {
			this.markFieldDirty("_updatedDate" as UnderscoreKeyNames<TFields>);
		}

		for (const fieldName of this._dirtyFields) {
			if (!(fieldName in this)) {
				throw new BasicError("Incorrect config", `Field (${String(fieldName)}) set as dirty but doesn't exist on (${this.constructor.name})`);
			}
			obj[fieldName] = (this as any)[fieldName];
		}

		return obj;
	}
	markFieldDirty(fieldName: UnderscoreKeyNames<TFields>): void {
		this._updatedDate = Date.now();
		if (!this._dirtyFields.includes(fieldName)) this._dirtyFields.push(fieldName);
		this._dirty = true;
	}
	markClean(): void {
		this._dirty = false;
		this._dirtyFields = [];
		this._countChanges = {};
		this._parentCountChanges = {};
	}
	isDirty(): boolean {
		return this._dirty;
	}
	get lockExpiresFormatted() {
		if (!this.lockExpires) return undefined;
		const dt = DateTime.fromMillis(this.lockExpires);
		return dt.setLocale("en-au").toLocaleString({ day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
	}
	set status(newStatus: number) {
		// Exit early if status hasn't changed or is invalid
		if (this._status === newStatus || !this.validateStatus(newStatus)) return;

		if (!this._parentCountChanges) {
			this._status = this.checkFieldTypeAndMark<"status">("_status", newStatus);
			return;
		}

		const prevKey = `${this._className}${this._status}`;
		const nextKey = `${this._className}${newStatus}`;

		if (this._status === undefined) {
			// Object has just been created so set total counts
			this._parentCountChanges[`ALL_${this._className}`] = 1;
			this._parentCountChanges[`CURRENT_${this._className}`] = 1;
		} else {
			// Decrease count for previous status
			this._parentCountChanges[prevKey] = (this._parentCountChanges[prevKey] || 0) - 1;
		}

		// Increase count for new status
		this._parentCountChanges[nextKey] = (this._parentCountChanges[nextKey] || 0) + 1;

		this._status = this.checkFieldTypeAndMark<"status">("_status", newStatus);
	}
	markDeletedStatus() {
		if (!this._parentCountChanges) return;

		const key = `${this._className}${this._status}`;

		// Decrease count for status
		if (this._status !== undefined) {
			this._parentCountChanges[key] = (this._parentCountChanges[key] || 0) - 1;
		}
		this._parentCountChanges[`CURRENT_${this._className}`] = -1;
	}

	get status(): number | undefined {
		return this._status;
	}
	validateStatus(status : number): boolean {
		//Override this method - 1 argument passed, status
		return true;
	}
	changeCount(key: string, delta: number, includeOnParent: boolean = true): void {
		if (delta === 0) return;

		const updateMap = (map: Record<string, number>, key: string, delta: number) => {
			map[key] = (map[key] || 0) + delta;
		};

		updateMap(this._counts as any, key, delta);
		updateMap(this._countChanges as any, key, delta);

		if (includeOnParent) {
			updateMap(this._parentCountChanges as any, key, delta);
		}
	}
	changeCounts(changes: Record<string, number>, includeOnParent: boolean = true): void {
		if (Object.keys(changes).length === 0) return;
		Object.entries(changes).forEach(([key, value]) => this.changeCount(key, value, includeOnParent));
	}
	setCount(key: string, value: number, includeOnParent = true): void {
		const current = (this._counts as any)[key] ?? 0;
		const delta = value - current;

		if (delta !== 0) {
			(this._countChanges as any)[key] = delta;
		} else {
			delete (this._countChanges as any)[key];
		}

		(this._counts as any)[key] = value;

		if (includeOnParent) {
			if (delta !== 0) {
				(this._parentCountChanges as any)[key] = delta;
			} else {
				delete (this._parentCountChanges as any)[key];
			}
		}
	}

	setCounts(counts: object, includeOnParent: boolean = true): void {
		if (Object.keys(counts).length === 0) return;
		Object.entries(counts).forEach(([key, value]) => this.setCount(key, value, includeOnParent));
	}
	hasCountChanges(): boolean {
		return Object.keys(this._countChanges as any).length > 0;
	}
	getCountChanges(): object | undefined {
		return this._countChanges;
	}
	getCountByKey(key: string, defaultValue = 0): number {
		return (this._counts as any)[key] ? (this._counts as any)[key] : defaultValue;
	}
	getAverageByKeys(keyNumerator: string, keyDenominator: string, decimals = 2, NaNReplacement: number = 0): number {
		return meanAverage(this.getCountByKey(keyNumerator), this.getCountByKey(keyDenominator), decimals, NaNReplacement);
	}
	getCountByKeyFilter(fnc: (key: string) => boolean): number {
		return Object.entries<number>(this._counts as any).reduce(
			(prevValue: number, [key, val]: [string, number]) => (fnc(key) ? prevValue + val : prevValue),
			0
		);
	}
	getParentCountChanges(): object | null {
		if (this._parentCountChanges) return filter(this._parentCountChanges, (val: number) => val !== 0);
		return null;
	}
	abstract getColumnsForExport(): Array<string>;
	abstract getObjectForExport(): object;
	isLocked(): boolean {
		return this.lockExpires !== undefined && this.lockExpires > Date.now();
	}
	hasLock(): boolean {
		return this.isLocked() && this.lockOwner === this.lockExpires;
	}
	/**
	 * Override if additional schema validation needed
	 */
	validateSchema() : void {
		baseDynamoSchema.parse(this);
	}
}
